'use client';

import { useId, type ReactNode } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Confetti, HandHeart, Heart, Star } from '@phosphor-icons/react';
import type { ReactionKind } from '@leanin/shared/reactions';
import { CircleStack, I, Icon, Label } from '@leanin/ui';
import { ToggleGroupPrimitive } from '@leanin/ui/client';
import styles from './ReactionPicker.module.css';

const reactionIconProps = { size: '100%', weight: 'fill' } as const;

// Relate's glyph: two people hugging (the 🫂 emoji's pose), a filled
// one-color shape so it sits in a disc like the other reactions and takes
// their ink. Neither Phosphor nor Lucide ships a hug. The mask cuts the seams
// that read as two figures: a ring around the front head, and the edges of
// that person's arm wrapping across the other's back. The viewBox pads the
// figure to the same optical size as the Phosphor glyphs beside it. The mask
// id is per instance — ids are document-global.
function HugGlyph() {
  const maskId = `hug-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const seam = { stroke: '#000', strokeWidth: 18, fill: 'none', strokeLinecap: 'round' } as const;
  return (
    <svg viewBox="60 50 650 650" width="100%" height="100%" fill="currentColor" aria-hidden="true">
      <mask id={maskId}>
        <rect x="60" y="50" width="650" height="650" fill="#fff" />
        <circle cx="268" cy="226" r="130" {...seam} />
        <path d="M146 356 C222 420 330 468 390 514 L394 650" {...seam} />
        <path d="M456 345 C522 386 592 396 650 380" {...seam} />
      </mask>
      <g mask={`url(#${maskId})`}>
        <circle cx="268" cy="226" r="118" />
        <circle cx="500" cy="222" r="108" />
        <path d="M190 640 C138 640 112 612 112 562 L112 492 C112 446 124 404 150 372 C174 344 206 330 246 330 L520 330 C586 318 632 342 646 382 C657 414 651 442 633 462 C651 490 658 528 658 566 C658 612 634 640 584 640 Z" />
      </g>
    </svg>
  );
}

export type { ReactionKind };

const ICON_TO_DISC_RATIO = 0.62;

type Reaction = {
  id: ReactionKind;
  label: string;
  icon: ReactNode;
  fill: string;
  ink: string;
};

// Keyed by kind so a render site indexes it directly; written in picker order,
// which `REACTIONS` keeps.
export const REACTION_BY_ID: { readonly [K in ReactionKind]: Reaction & { id: K } } = {
  support:    { id: 'support',    label: 'Love',      icon: <Heart     {...reactionIconProps} />, fill: '#ffe4e6', ink: '#be123c' },
  helpful:    { id: 'helpful',    label: 'Celebrate', icon: <Confetti  {...reactionIconProps} />, fill: '#ffedd5', ink: '#ea580c' },
  insightful: { id: 'insightful', label: 'Helpful',   icon: <Star      {...reactionIconProps} />, fill: '#fef3c7', ink: '#ca8a04' },
  same:       { id: 'same',       label: 'Support',   icon: <HandHeart {...reactionIconProps} />, fill: '#ede9fe', ink: '#7c3aed' },
  // "I've been there too."
  relate:     { id: 'relate',     label: 'Relate',    icon: <HugGlyph />,                         fill: '#ccfbf1', ink: '#0f766e' },
};

export const REACTIONS: ReadonlyArray<Reaction> = Object.values(REACTION_BY_ID);

// The kind (Love) a plain click on an un-reacted button applies — the picker
// handles the others. Shared so every reaction surface agrees on it.
export const DEFAULT_REACTION: ReactionKind = 'support';

// The un-reacted reaction icon: an outline heart in the same style as the
// Comment action's speech bubble. Lucide's heart fills less of its 24px box
// than the bubble does, so at the same 16px slot it reads smaller — scaled up
// to match optically.
export const OUTLINE_HEART = (
  <span style={{ display: 'inline-flex', width: '100%', height: '100%', transform: 'scale(1.125)' }}>
    {I.heart}
  </span>
);

export const REACTION_LABEL_KEY = {
  support: 'reactions.love',
  helpful: 'reactions.celebrate',
  insightful: 'reactions.helpful',
  same: 'reactions.support',
  relate: 'reactions.relate',
} as const satisfies Record<ReactionKind, string>;

function topReactions(
  counts: Record<ReactionKind, number>,
  mine: ReactionKind | null | undefined,
  limit: number,
): ReadonlyArray<Reaction> {
  const order = new Map(REACTIONS.map((r, i) => [r.id, i]));
  return REACTIONS
    .filter((r) => counts[r.id] > 0)
    .sort((a, b) => {
      if (a.id === mine) return -1;
      if (b.id === mine) return 1;
      const cmp = counts[b.id] - counts[a.id];
      return cmp !== 0 ? cmp : (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0);
    })
    .slice(0, limit);
}

export function ReactionDisc({
  reaction,
  size,
  active = false,
}: {
  reaction: Reaction;
  size: number;
  /** When true, swap fill↔ink for a "selected" inverted appearance. */
  active?: boolean;
}) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--radius-full)',
        background: active ? reaction.ink : reaction.fill,
        color: active ? reaction.fill : reaction.ink,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={Math.round(size * ICON_TO_DISC_RATIO)}>
        {reaction.icon}
      </Icon>
    </span>
  );
}

export function ReactionStack({
  counts,
  mine,
  size = 22,
  max = 3,
}: {
  counts: Record<ReactionKind, number>;
  mine: ReactionKind | null | undefined;
  size?: number;
  max?: number;
}) {
  const items = topReactions(counts, mine, max);
  if (items.length === 0) return null;
  return (
    <CircleStack
      items={items}
      size={size}
      renderItem={(r) => (
        <ReactionDisc reaction={r} size={size} />
      )}
    />
  );
}

export function ReactionPicker({
  mine,
  onPick,
}: {
  mine: ReactionKind | null | undefined;
  onPick: (kind: ReactionKind) => void;
}) {
  const t = useTranslations('Feed');
  return (
    <ToggleGroupPrimitive.Root
      type="single"
      // '' (not undefined) keeps the group controlled when nothing is
      // selected — undefined flips Radix to uncontrolled and back.
      value={mine ?? ''}
      onValueChange={(value: string) => {
        // Radix reports re-clicking the selected item as ''. That tap means
        // "remove my reaction" — forward the held kind and let the server
        // toggle delete it. This is the only unreact path now that the
        // summary pill's click opens the roster.
        if (value === '') {
          if (mine != null) onPick(mine);
          return;
        }
        onPick(value as ReactionKind);
      }}
      aria-label={t('reactions.pickerAria')}
      className={styles.root}
    >
      {REACTIONS.map((r) => (
        <ToggleGroupPrimitive.Item
          key={r.id}
          value={r.id}
          aria-label={t(REACTION_LABEL_KEY[r.id])}
          className={styles.item}
        >
          {/* LinkedIn-style name above the hovered (or focused) icon. The
              item's aria-label already names it for assistive tech. */}
          <span className={styles.label} aria-hidden="true">
            <Label size="sm">{t(REACTION_LABEL_KEY[r.id])}</Label>
          </span>
          <span className={styles.disc}>
            <ReactionDisc reaction={r} size={36} active={r.id === mine} />
          </span>
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}
